import { Directive, ContentChildren, QueryList, Input, Output, EventEmitter, SimpleChanges, Host, ElementRef, ChangeDetectorRef } from '@angular/core';
import { sleep } from '@cf-shared/shared-util';
import { VldGroupDirective } from 'directives/vld-group/vld-group';
import { VldGroupState } from 'directives/vld-group/vld-group-interface';
import { LoadingController } from 'providers/loading-controller';
import { LoggingService } from 'services/logging.service';
import { describeError } from '@cf-shared/error-describe';

@Directive({
  selector: '[vld-progress]',
  standalone: false,
})
export class VldProgressDirective {
  @ContentChildren(VldGroupDirective, { descendants: true }) groups: QueryList<VldGroupDirective>;

  @Input('vldTotalSteps') totalSteps: number = 0;
  @Input('vldStep') currentStep: number;
  @Input('vldAutoScroll') autoScroll: boolean = false;
  @Input('vldDisableHiding') disableHiding: boolean = false;

  @Output('vldStepChange') currentStepChange: EventEmitter<number> = new EventEmitter<number>();
  @Output('vldTotalStepsChange') totalStepsChange: EventEmitter<number> = new EventEmitter<number>();

  maxStep: number; // farthest the user ever got
  private abortController: AbortController | null = null;
  private isDestroyed = false;

  constructor(
    @Host() private hostElem: ElementRef,
    private loadingController: LoadingController,
    private cd: ChangeDetectorRef,
    private logging: LoggingService,
  ) { }

  ngAfterContentInit() {
    this.totalSteps = 0;
    let addOne = (g: VldGroupDirective) => {
      g.progress = this;
      g.stepNumber = ++this.totalSteps;
    };

    this.groups.forEach(addOne);
    this.groups.changes.subscribe(addOne);

    // this is a weird cludge... here's what it's about:
    // 1. Angular will not initialize the QueryList above until ngAfterContentInit().
    // 2. We need the contents of the QueryList to generate the value of currentStep and totalSteps
    // 3. those are @Output vars and can be bound to some other control (that's the purpose actually)
    // 4. Thus some other control's input gets changed during ngAfterContentInit()
    // 5. that's not allowed -> Angular complains: "Expression has changed after it was checked"
    // So, the solution (proposed here: https://blog.angular-university.io/angular-debugging/ ) is
    // to postpone the update until the next tick. Yes the view will see the initial (empty) data for one frame.
    // looking forward to a better solution.
    setTimeout(() => {
      if (!this.isDestroyed) {
        this.onFirstInit();
      }
    });
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  // when the form is loaded
  onFirstInit() {
    if (this.isDestroyed) return;
    
    this.maxStep = this.currentStep; // resume where we left off (assuming the data is still valid)
    // first ask all the groups to check their values and set their states
    this.groups.forEach(g => g.onFirstInit());
    // now run normal change detection again -> this will determine the current step and then reupdate them
    this.onProgressChanged();

    // Cancel any previous async operations
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    // create async task
    void (async () => {
      try {
        // Check if aborted before starting
        if (signal.aborted || this.isDestroyed) return;
        
        // Find last done group and scroll to it
        const lastGroupDoneGroup = this.groups.find(g => g.state === VldGroupState.Ready) || this.groups.toArray().reverse().find(g => g.state === VldGroupState.Done);
        if (lastGroupDoneGroup) {
          // First trigger change detection to ensure DOM is updated
          this.cd.markForCheck();
          // Small delay to allow DOM updates to propagate
          await sleep(0);
          
          // Check if aborted after async operation
          if (signal.aborted || this.isDestroyed) return;
          
          await this.waitForAnimationsRecursive(lastGroupDoneGroup.element.nativeElement as HTMLElement, signal);
          
          // Check if aborted after animations
          if (signal.aborted || this.isDestroyed) return;

          void this.scrollIfNeededAsync(lastGroupDoneGroup.element.nativeElement as HTMLElement, signal);
        }
      } catch (e) {
        // an abort is this directive being destroyed or a newer step taking over, not a failure.
        // anything else left the view unscrolled to the step the person just finished
        if (e?.name !== 'AbortError') {
          this.logging.trackSilentProblem('VldProgressScrollFailed', { phase: 'onFirstInit', error: describeError(e) });
        }
      }
    })();
  }

  ngOnChanges(changes: SimpleChanges) {
    void changes;
    if (changes.currentStep && changes.currentStep.currentValue < changes.currentStep.previousValue) {
      // mark all previous groups as non done
      this.groups.forEach(g => {
        if (g.stepNumber >= changes.currentStep.currentValue) {
          g.state = VldGroupState.Uninitialized;
        }
      });
      this.maxStep = changes.currentStep.currentValue;
      this.onProgressChanged();
    }
  }

  onProgressChanged() {
    if (!this.groups || this.isDestroyed) {
      return; // no groups yet or destroyed, nothing to do
    }

    // find first non-done group
    let groupNotDone = this.groups.find(g => g.state != VldGroupState.Done);
    if (groupNotDone == null) {
      return; // this happens in pages that use the last "next" button to react to that click and save
    }

    if (this.maxStep == null) {
      this.maxStep = 0;
    }

    // that determines current step
    this.currentStep = groupNotDone.stepNumber;
    this.currentStepChange.emit(this.currentStep);

    this.totalSteps = this.groups.length;
    this.totalStepsChange.emit(this.totalSteps);

    if (this.currentStep > this.maxStep) {
      this.maxStep = this.currentStep;
    }

    this.groups.forEach(g => g.onProgressChanged());

    const lastGroupDoneGroup = this.groups.find(g => g.state < VldGroupState.Done) || this.groups.toArray().reverse().find(g => g.state === VldGroupState.Done);
    if (lastGroupDoneGroup) {
      if (lastGroupDoneGroup.state === VldGroupState.Ready || lastGroupDoneGroup.state === VldGroupState.Done) {
        // Cancel any previous async operations
        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();
        const signal = this.abortController.signal;
        
        // create async task
        void (async () => {
          try {
            // Check if aborted before starting
            if (signal.aborted || this.isDestroyed) return;
            
            // First trigger change detection to ensure DOM is updated
            this.cd.markForCheck();
            // Small delay to allow DOM updates to propagate
            await sleep(0);
            
            // Check if aborted after async operation
            if (signal.aborted || this.isDestroyed) return;
            
            await this.waitForAnimationsRecursive(lastGroupDoneGroup.element.nativeElement as HTMLElement, signal);
            
            // Check if aborted after animations
            if (signal.aborted || this.isDestroyed) return;

            void this.scrollIfNeededAsync(lastGroupDoneGroup.element.nativeElement as HTMLElement, signal);
          } catch (e) {
            // an abort is this directive being destroyed or a newer step taking over, not a failure.
            // anything else left the view unscrolled to the step the person just finished
            if (e?.name !== 'AbortError') {
              this.logging.trackSilentProblem('VldProgressScrollFailed', { phase: 'onProgressChanged', error: describeError(e) });
            }
          }
        })();
      } else {
        void this.scrollIfNeededAsync(lastGroupDoneGroup.element.nativeElement as HTMLElement);
      }
    }
  }

  async waitForAnimationsRecursive(target: HTMLElement, signal?: AbortSignal) {
    // Check if operation was aborted
    if (signal?.aborted || this.isDestroyed) {
      throw new DOMException('Operation aborted', 'AbortError');
    }
    
    // Collect all animation promises
    const animationPromises: Promise<any>[] = [];

    // Check for CSS transitions on the element
    const computedStyle = window.getComputedStyle(target);
    const transitionDuration = computedStyle.transitionDuration;
    const transitionDelay = computedStyle.transitionDelay;

    // Parse transition duration and delay (can be comma-separated for multiple properties)
    const getDurationMs = (timeStr: string): number => {
      if (!timeStr || timeStr === 'none' || timeStr === '0s') return 0;
      const times = timeStr.split(',').map(t => {
        const trimmed = t.trim();
        if (trimmed.endsWith('ms')) {
          return parseFloat(trimmed);
        } else if (trimmed.endsWith('s')) {
          return parseFloat(trimmed) * 1000;
        }
        return 0;
      });
      return Math.max(...times);
    };

    const totalDuration = getDurationMs(transitionDuration);
    const totalDelay = getDurationMs(transitionDelay);
    const totalTransitionTime = totalDuration + totalDelay;

    if (totalTransitionTime > 0) {
      // Wait for transition to complete
      animationPromises.push(
        new Promise((resolve, reject) => {
          const timeout = setTimeout(resolve, totalTransitionTime);
          
          // Listen for abort signal
          if (signal) {
            signal.addEventListener('abort', () => {
              clearTimeout(timeout);
              reject(new DOMException('Operation aborted', 'AbortError'));
            }, { once: true });
          }
        })
      );
    }

    // Check for Web Animations API animations
    if ('getAnimations' in target) {
      const animations = (target as any).getAnimations();
      if (animations && animations.length > 0) {
        // Catch cancellation errors - animations can be cancelled during navigation
        animationPromises.push(...animations.map((a: any) => a.finished.catch(() => {})));
      }
    }

    // Recursively check children
    for (let iChild = 0; iChild < target.children.length; iChild++) {
      const child = target.children[iChild];
      if (child instanceof HTMLElement) {
        animationPromises.push(this.waitForAnimationsRecursive(child, signal));
      }
    }

    // Wait for all animations to complete
    if (animationPromises.length > 0) {
      await Promise.all(animationPromises);
    }

    // In zoneless mode, we need to manually trigger change detection after animations complete
    this.cd.markForCheck();
  }

  /** Force scroll to element, bypassing autoScroll check. Used for validation errors. */
  async scrollToElementAsync(target?: HTMLElement, signal?: AbortSignal) {
    await this.scrollIfNeededAsync(target, signal, true);
  }

  /** If event is provided, scroll to a bit below those coordinates, so make sure the rest of the form is visible.  */
  async scrollIfNeededAsync(target?: HTMLElement, signal?: AbortSignal, forceScroll: boolean = false) {
    if (!forceScroll && !this.autoScroll) {
      return;
    }

    // Don't auto-scroll if virtual keyboard is active
    // Check if any input-measure component has virtual keyboard open
    const virtualKeyboard = document.querySelector('virtual-keyboard');
    if (virtualKeyboard) {
      // Virtual keyboard is visible, don't auto-scroll as user is actively typing
      return;
    }

    // Also check if an input inside the target has focus (user might be typing)
    if (target) {
      const activeElement = document.activeElement;
      if (activeElement && activeElement.tagName === 'INPUT' && target.contains(activeElement)) {
        // Input has focus within this group, don't auto-scroll
        return;
      }
    }

    try {
      // Wait for any recursive child to update any transition or animation
      if (target) {
        await this.waitForAnimationsRecursive(target, signal);
        await this.loadingController.isLoadingPromise();
        // Trigger change detection after all async operations complete
        this.cd.markForCheck();
      }

      let hostElement = this.hostElem.nativeElement as HTMLElement;
      void hostElement;
      // let scrollElement = hostElement.closest('.scroll-area') as HTMLElement;
      // let y = event && event.srcElement && (event.srcElement as HTMLElement).getBoundingClientRect().bottom;
      // let scrollToY;
      // if (y) {
      // // this adjusts the scroll so that the 'event' element is in the middle of the screen (if scroll area size allows it)
      //   scrollToY = scrollElement.scrollTop + (y + scrollElement.clientHeight / 2 - scrollElement.clientHeight);
      // } else {
      //   // otherwise, scroll to the bottom
      //   scrollToY = scrollElement.scrollHeight;
      // }
      // scrollElement.scrollTo({ top: scrollToY, behavior: 'smooth' });

      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    } catch (e) {
      // Ignore abort errors - these are expected when operations are cancelled
      if (e?.name !== 'AbortError') {
        // Re-throw other errors
        throw e;
      }
    }
  }
}
